import React, { FC, ReactNode, useRef } from "react";
import Button, { ButtonSize } from "../Button/Button";
import TextInput, { TextInputSize } from "../TextInput/TextInput";
import CopyToClipboard from "../../behaviors/CopyToClipboard/CopyToClipboard";
import { randomString } from "../../../utils";

interface ICopyInputProps {
  value: string;
  label: string;
  labelHidden?: boolean;
  buttonContent?: ReactNode;
  size?: TextInputSize.Small | TextInputSize.Medium | TextInputSize.Large;
  onCopy?: () => void;
}

/**
 * A combined readonly TextInput and Button to copy a value to the clipboard
 */
const CopyInput: FC<ICopyInputProps> = ({
  value,
  onCopy: onCopyCallback,
  label,
  labelHidden,
  size = TextInputSize.Medium,
  buttonContent = "Copy",
}) => {
  const copyInputRef = useRef<HTMLInputElement>(null);
  const copyInputId = useRef(`copy-input-${randomString()}`);

  const handleCopy = (onCopy: () => void): void => {
    onCopyCallback?.();
    onCopy();
  };

  const handleInputFocus = (): void => {
    copyInputRef.current?.select();
  };

  return (
    <TextInput
      readOnly
      className="kl-copy-input"
      id={copyInputId.current}
      ref={copyInputRef}
      onFocus={handleInputFocus}
      labelHidden={labelHidden}
      value={value}
      label={label}
      size={size}
      endElement={
        <CopyToClipboard showTooltipOnCopy value={value}>
          {({ onCopy }): ReactNode => (
            <Button
              aria-labelledby={`${copyInputId.current}-label`}
              size={getButtonSize(size)}
              onClick={(): void => handleCopy(onCopy)}
            >
              {buttonContent}
            </Button>
          )}
        </CopyToClipboard>
      }
    />
  );
};

// Returns a button one size smaller than the input size
const getButtonSize = (inputSize: TextInputSize): ButtonSize => {
  const sizeMap = new Map([
    [TextInputSize.Small, ButtonSize.XSmall],
    [TextInputSize.Medium, ButtonSize.Small],
    [TextInputSize.Large, ButtonSize.Medium],
  ]);

  return sizeMap.get(inputSize) || ButtonSize.Small;
};

export default CopyInput;
